import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useQuotes } from '../context/QuotesContext';
import { useTranslation } from '../context/TranslationContext';
import { Trash2, Pencil, Save, Shield, Wallet, Eye, Leaf, Mail, FileDown, Printer, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Checkbox } from './ui/checkbox';
import { Check } from 'lucide-react';
import ConfiguratorNavActions from './ConfiguratorNavActions';
import { 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  raamtypes, 
  hardware, 
  accessories, 
  woodJointOptions,
  getColorById 
} from '../data/mockData';
import { getColorByRAL } from '../data/ralColors';
import { API_URL } from '../config/api';
import {
  resolveConfiguratorContactState,
  applyActiveContactToClientDetails,
  persistActiveContactForConfigurator,
  applyExplicitContactToBackendQuoteSession,
  buildSelectedContactFromClientDetails,
  formatQuoteClientDisplayName,
} from '../lib/contactConfigurator';
import { resolveQuoteAdvisorName, formatPersonName } from '../lib/formatPersonName';
import ContactSearchPicker from './ContactSearchPicker';
import VmkQuotePricingPage, { VmkQuotePageFooter } from './VmkQuotePricingPage';
import { VmkQuoteTemplateFront, VmkQuoteTemplateBack, VmkInvestmentPage } from './VmkQuoteTemplatePages';
import { exportPdfPages, printQuotePages, renumberQuotePageFooters } from '../lib/pdfPageExport';
import { QuotePreviewScaler } from './QuotePreviewScaler';
import { packQuotesIntoPages } from '../lib/vmkQuotePagePack';
import {
  VMK_DRAWING_BASE_PAGE,
  getDrawingPageNumber,
  getTailPageNumber,
} from '../lib/vmkQuoteDocumentLayout';
import { getTotalQuotesAreaSqm } from '../lib/vmkQuoteTable';
import { enhanceQuoteDrawingSvg } from '../lib/enhanceQuoteDrawingSvg';
import { generateQuoteNumber, formatQuoteDisplayReference } from '../lib/quoteNumber';
import { isEditingBackendQuote, canOverwriteBackendQuote, shouldSaveBackendQuoteAsNew, resolveQuoteSendPersistence, isBackendQuoteSigned, resolveQuoteContactId, withQuoteCreator, preserveCreatorFromBackendQuote, serializeQuoteDrawingsPayload, computeStoredQuoteTotalInclVat } from '../lib/quoteSession';
import { buildExportSectionList, isMultiSectionQuote, countQuoteDrawings } from '../lib/quoteSections';
import { isValidEmail, formatApiErrorDetail } from '../lib/emailValidation';
import { isAdminUser } from '../lib/userRoles';
import { buildQuotesListBackState } from '../lib/quoteListPagination';
import { applyDeductionMargin, applyExportPriceAdjustment, clampExportPriceAdjustmentEur, parseExportPriceAdjustmentAmount, MAX_USER_PRICE_REDUCTION_EUR, getExportPriceAdjustmentState, getQuotePriceAdjustmentInclVat, getQuoteExtraLineItemsExVat, getQuoteExtraLineItemsInclVat } from '../lib/pricingAdjustments';
import { createAdditionalItem, parseAdditionalItemPrice } from '../lib/additionalItems';
import { getQuoteProjectFeesExVat, BTW_MULTIPLIER_INCL } from '../lib/pdfPricing';
import '../styles/quotePdf.css';

// Yes/No Toggle Component
const YesNoToggle = ({
  label,
  value,
  onChange,
  yesLabel = 'YES',
  noLabel = 'NO',
  disabled = false,
}) => {
  const isYes = value === true;
  const isNo = value === false;

  return (
    <div className={`flex items-center justify-between p-2 border border-gray-200 rounded-md bg-white transition-colors ${disabled ? 'opacity-70' : 'hover:bg-gray-50'}`}>
      <Label className="text-xs text-gray-700 cursor-pointer flex-1">{label}</Label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(true)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors disabled:cursor-not-allowed ${
            isYes
              ? 'bg-green-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {yesLabel}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(false)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors disabled:cursor-not-allowed ${
            isNo
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {noLabel}
        </button>
      </div>
    </div>
  );
};

const SavedQuotes = ({
  asPage = false,
  dashboardMode = false,
  forceEditing = false,
  open = true,
  onOpenChange,
  drawingBoardRef,
  returnPath = '/configurator-select',
  returnQuotesPage = null,
}) => {
  const { t, language } = useTranslation();
  const {
    savedQuotes,
    quoteSections,
    removeQuote,
    clearAllQuotes,
    updateQuote,
    includePricingInExport,
    includeTemplateInExport,
    setIncludePricingInExport,
    setIncludeTemplateInExport,
    includeNijBegunInExport,
    setIncludeNijBegunInExport,
    includeTotalPageInExport,
    setIncludeTotalPageInExport,
    activeBackendQuote,
    patchActiveBackendQuote,
    loadBackendQuoteSession,
  } = useQuotes();
  const { isAuthenticated, user } = useAuth();
  const isAdmin = isAdminUser(user);
  const quoteIsSigned = isBackendQuoteSigned(activeBackendQuote);
  const canUpdateExistingQuote = canOverwriteBackendQuote(activeBackendQuote);
  const saveAsNewQuote = shouldSaveBackendQuoteAsNew(activeBackendQuote);
  const navigate = useNavigate();
  const location = useLocation();
  const goBackToList = () => {
    navigate(returnPath, {
      state: buildQuotesListBackState(returnPath, returnQuotesPage),
    });
  };
  const isActive = asPage || open;
  const [pricingSettings, setPricingSettings] = useState(null);
  const [savedQuoteDiscountEnabled, setSavedQuoteDiscountEnabled] = useState(false);
  const [savedQuoteDiscountPercentage, setSavedQuoteDiscountPercentage] = useState(0);
  const [clientDetails, setClientDetails] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    notes: '',
    signature: '',
    subjectToFinance: false, // Subject to finance option
    // Additional Options
    clickOnColorAndSize: false,
    screenElectricallyOperated: false,
    rollerShutterElectricallyOperated: false,
    // Home Finishing
    buoySharing: false,
    typesetting: false,
    container: false,
    interiorFinish: false,
    // Lifting Support
    crane: false,
    craneTrips: '',
    liftingAssistance: false,
    liftingAssistancePersonnel: '',
    // Comments
    comments: '',
    // Admin export-quote fixed deduction (EUR, max 500)
    exportDeductionEnabled: false,
    exportDeductionAmount: 0,
    // Price adjustment: add or subtract (amount always >= 0, mode add|subtract)
    exportPriceAdjustmentEnabled: false,
    exportPriceAdjustmentAmount: 0,
    exportPriceAdjustmentMode: 'subtract',
    // Quote-level extra line items (description + price incl. BTW)
    extraLineItems: [],
  });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const quoteRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const actionButtonsRef = useRef(null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [tableSvgData, setTableSvgData] = useState(null);
  const [signLater, setSignLater] = useState(false);
  const [signLaterEmail, setSignLaterEmail] = useState('');
  const [signLaterEmailSent, setSignLaterEmailSent] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [sendEmailTo, setSendEmailTo] = useState('');
  const [extraItemPriceDrafts, setExtraItemPriceDrafts] = useState({});
  const [sendingQuoteEmail, setSendingQuoteEmail] = useState(false);
  const sendingQuoteEmailRef = useRef(false);
  const [sendEmailDialogSuccess, setSendEmailDialogSuccess] = useState(false);
  const [sendEmailError, setSendEmailError] = useState('');

  // Generate quote number and sync client details when export view opens
  useEffect(() => {
    if (!isActive) return;

    if (activeBackendQuote?.quoteNumber) {
      setQuoteNumber(activeBackendQuote.quoteNumber);
    } else {
      setQuoteNumber(generateQuoteNumber());
    }

    const editingExistingQuote = isEditingBackendQuote(activeBackendQuote);

    setClientDetails((prev) => {
      let next = activeBackendQuote?.clientDetails
        ? { ...prev, ...activeBackendQuote.clientDetails }
        : { ...prev };

      // Migrate legacy deduction → price adjustment fields
      if (
        next.exportPriceAdjustmentEnabled !== true &&
        next.exportDeductionEnabled === true
      ) {
        next = {
          ...next,
          exportPriceAdjustmentEnabled: true,
          exportPriceAdjustmentMode: 'subtract',
          exportPriceAdjustmentAmount: Number(next.exportDeductionAmount) || 0,
        };
      }

      // Keep each saved quote's own client; only pre-fill CRM contact for new offers.
      if (isAuthenticated && !editingExistingQuote) {
        const selectedContact = resolveConfiguratorContactState(location.state, user?.id);
        if (selectedContact) {
          next = applyActiveContactToClientDetails(next, selectedContact, {
            preferActiveContact: Boolean(location.state?.selectedContact),
          });
        }
      }

      return next;
    });

    if (asPage) {
      setIsEditing(dashboardMode ? false : forceEditing);
      setIsSaved(true);
    } else {
      setIsEditing(true);
      setIsSaved(false);
    }
    setTableSvgData(savedQuotes.length > 0 && savedQuotes[0].svgData ? savedQuotes[0].svgData : null);
  }, [
    isActive,
    asPage,
    forceEditing,
    dashboardMode,
    activeBackendQuote?.id,
    activeBackendQuote?.clientDetails,
    activeBackendQuote?.clientContactOverride,
    isAuthenticated,
    user?.id,
    location.state,
  ]);

  useEffect(() => {
    if (!isActive || !activeBackendQuote?.id) return;
    if (activeBackendQuote.created_at && activeBackendQuote.valid_until) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/quotes/${activeBackendQuote.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const created_at = res.data?.created_at;
        const valid_until = res.data?.valid_until;
        if (!created_at && !valid_until) return;
        patchActiveBackendQuote({
          ...(created_at ? { created_at } : {}),
          ...(valid_until ? { valid_until } : {}),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    isActive,
    activeBackendQuote?.id,
    activeBackendQuote?.created_at,
    activeBackendQuote?.valid_until,
    patchActiveBackendQuote,
  ]);

  // When switching to preview, scroll action buttons (Print, Export, Save) into view so they stay visible
  useEffect(() => {
    if (!isEditing && isSaved && actionButtonsRef.current) {
      const t = setTimeout(() => actionButtonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
      return () => clearTimeout(t);
    }
  }, [isEditing, isSaved]);

  // Initialize signature canvas
  useEffect(() => {
    if (signatureCanvasRef.current && isEditing) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (clientDetails.signature) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = clientDetails.signature;
      }
    }
  }, [isEditing, clientDetails.signature]);

  // Fetch pricing settings when export view opens (not needed on dashboard quote viewer)
  useEffect(() => {
    if (isActive && isAuthenticated && !dashboardMode) {
      axios
        .get(`${API_URL}/api/pricing-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        })
        .then((response) => {
          setPricingSettings(response.data);
        })
        .catch(() => {
          setPricingSettings(null);
        });
    }
  }, [isActive, isAuthenticated, dashboardMode]);

  // Dashboard quote viewer still needs pricing settings for table totals
  useEffect(() => {
    if (!isActive || !isAuthenticated || !dashboardMode || pricingSettings) return;
    axios
      .get(`${API_URL}/api/pricing-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      })
      .then((response) => {
        setPricingSettings(response.data);
      })
      .catch(() => {
        setPricingSettings(null);
      });
  }, [isActive, isAuthenticated, dashboardMode, pricingSettings]);

  const handleContactSelect = (contact) => {
    if (!contact) return;
    const payload = persistActiveContactForConfigurator(contact, user?.id);
    const fullName = formatPersonName(`${contact.first_name} ${contact.last_name}`.trim());
    const nextDetails = {
      ...clientDetails,
      name: fullName,
      address: contact.address || '',
      city: contact.city || '',
      postalCode: contact.postal_code || '',
      phone: contact.phone || '',
      email: contact.email || '',
      contactId: contact.id,
    };
    setClientDetails(nextDetails);
    if (isEditingBackendQuote(activeBackendQuote)) {
      const patch =
        applyExplicitContactToBackendQuoteSession(activeBackendQuote, payload, {
          isEditingExisting: true,
        }) || { clientDetails: nextDetails, clientContactOverride: true };
      patchActiveBackendQuote(patch);
    }
  };

  // Format date
  const formatDate = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const d = new Date(date);
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Generate description items from saved quote
  const getDescriptionItemsForQuote = (quote) => {
    const items = [];
    const config = quote.config;
    
    // Dimensions
    items.push(`- ${t('common.width')}: ${config.width} mm`);
    items.push(`- ${t('common.height')}: ${config.height} mm`);
    items.push(`- ${t('common.area')}: ${((config.width * config.height) / 1000000).toFixed(2)} m²`);
    
    // Detect page type – Achterdeur before Terrasdeur (Achterdeur config also has designType)
    const pageType = config.doorStyle !== undefined ? 'voordeur' :
                     config.topLight !== undefined || config.numberOfParts !== undefined ? 'achterdeur' :
                     config.designType !== undefined ? 'terrasdeur' :
                     config.doorType === 'sliding' || config.slidingPosition ? 'schuifpui' :
                     'kozijnen';
    
    // Profile System
    if (profileSystems && profileSystems.length > 0 && config.profileSystem) {
      const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
      if (profileSystem) {
        items.push(`- ${t('options.profileFrames', { name: profileSystem.name })}`);
      }
    }
    
    // External Frame Color
    const externalColorId = config.exteriorColor || config.color;
    if (externalColorId) {
      const colorData = getColorById(externalColorId);
      const colorName = colorData?.name || (colorData?.ral ? `RAL ${colorData.ral}` : externalColorId);
      items.push(`- ${t('options.exteriorColor')} ${colorName}`);
    }
    
    // Interior Frame Color (for windows)
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    if (hasInteriorFrames && config.interiorFrameColor) {
      const interiorColorData = getColorById(config.interiorFrameColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorFrameColor);
      items.push(`- ${t('options.interiorColor')} ${interiorColorName}`);
    }
    
    // Interior Color (for doors)
    if (config.interiorColor && (pageType === 'schuifpui' || pageType === 'achterdeur' || pageType === 'voordeur' || pageType === 'terrasdeur')) {
      const interiorColorData = getColorById(config.interiorColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
      items.push(`- ${t('options.interiorColor')} ${interiorColorName}`);
    }
    
    // Glass Texture
    if (config.glassTexture) {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      if (glassTexture) {
        items.push(`- ${t('options.glassWithTexture', { name: glassTexture.name })}`);
      }
    }
    
    // Glazing Type
    if (config.glazingType) {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      if (glazingType) {
        items.push(`- ${glazingType.name}`);
      }
    }
    
    // Sliding Door (Schuifpui) specific
    if (pageType === 'schuifpui' || config.doorType === 'sliding') {
      if (config.slidingPosition) {
        const slidingPositionTranslations = {
          'left': t('options.slidingLeft'),
          'right': t('options.slidingRight'),
          'center': t('options.slidingCenter')
        };
        items.push(`- ${t('options.slidingPosition')}: ${slidingPositionTranslations[config.slidingPosition] || config.slidingPosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.leftGlassTexture) {
        const leftGlassTexture = glassTextures.find(g => g.id === config.leftGlassTexture);
        if (leftGlassTexture) {
          items.push(`- ${t('options.glassLeftPart')}: ${leftGlassTexture.name}`);
        }
      }
      if (config.rightGlassTexture) {
        const rightGlassTexture = glassTextures.find(g => g.id === config.rightGlassTexture);
        if (rightGlassTexture) {
          items.push(`- ${t('options.glassRightPart')}: ${rightGlassTexture.name}`);
        }
      }
      if (config.rightGlazingType) {
        const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
        if (rightGlazingType) {
          items.push(`- ${t('options.glazingTypeRightPart')}: ${rightGlazingType.name}`);
        }
      }
    }
    
    // Back Door (Achterdeur) specific
    if (pageType === 'achterdeur') {
      if (config.topLight) {
        const topLightTranslations = {
          'no-top-light': t('options.noTopLight'),
          'with-top-light': t('options.withTopLight')
        };
        items.push(`- ${t('options.topLight')}: ${topLightTranslations[config.topLight] || config.topLight}`);
      }
      if (config.numberOfParts) {
        const numberOfPartsTranslations = {
          'one-part': t('options.onePart'),
          'two-parts': t('options.twoParts')
        };
        items.push(`- ${t('options.numberOfParts')}: ${numberOfPartsTranslations[config.numberOfParts] || config.numberOfParts}`);
      }
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypeTranslations = {
          'sandwich-no-mullions': t('options.sandwichNoMullions'),
          'glass': t('options.glass'),
          'horizontal-mullions': t('options.horizontalMullions'),
          'vertical-mullions': t('options.verticalMullions')
        };
        items.push(`- ${t('options.panelType')}: ${panelTypeTranslations[config.panelType] || config.panelType}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- ${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Front Door (Voordeur) specific
    if (pageType === 'voordeur') {
      if (config.doorStyle) {
        const doorStyleTranslations = {
          'style-1': t('options.style1'),
          'style-2': t('options.style2'),
          'style-3': t('options.style3'),
          'style-4': t('options.style4'),
          'style-5': t('options.style5'),
          'style-6': t('options.style6')
        };
        items.push(`- ${t('options.doorStyle')}: ${doorStyleTranslations[config.doorStyle] || config.doorStyle}`);
      }
      if (config.parapet) {
        const handleTranslations = {
          'standard-knob': t('options.standardDoorKnob'),
          'rvs-bar': t('options.rvsBar')
        };
        items.push(`- ${t('options.handle')}: ${handleTranslations[config.parapet] || config.parapet}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
    }
    
    // Terrace Door (Terrasdeur) specific
    if (pageType === 'terrasdeur') {
      if (config.designType) {
        const designTypeTranslations = {
          'no-panel': t('options.noPanel'),
          'left-panel': t('options.leftPanel'),
          'right-panel': t('options.rightPanel'),
          'both-panels': t('options.bothPanels')
        };
        items.push(`- ${t('options.design')}: ${designTypeTranslations[config.designType] || config.designType}`);
      }
      if (config.doorType) {
        const doorTypeTranslations = {
          'normal': t('options.normal'),
          'top-light': t('options.topLight')
        };
        items.push(`- ${t('options.doorType')}: ${doorTypeTranslations[config.doorType] || config.doorType}`);
      }
      if (config.handlePosition) {
        const handlePositionTranslations = {
          'left': t('options.handleLeft'),
          'right': t('options.handleRight')
        };
        items.push(`- ${t('options.handlePosition')}: ${handlePositionTranslations[config.handlePosition] || config.handlePosition}`);
      }
      if (config.thresholdHeight) {
        items.push(`- ${t('options.threshold')}: ${config.thresholdHeight}`);
      }
      if (config.parapet) {
        items.push(`- ${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`);
      }
      items.push(`- ${t('common.security')}: ${t('common.securityRating')}`);
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
          }
        });
      }
    }
    
    // Window configurator (Kozijnen) specific
    if (pageType === 'kozijnen') {
      if (config.divisionType) {
        items.push(`- Division: ${config.divisionType}`);
      }
      if (config.partWindowTypes && config.partWindowTypes.length > 0) {
        config.partWindowTypes.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.overhead === 'overhead') {
        items.push('- Overhead');
        if (config.overheadParts > 1) {
          items.push(`- ${config.overheadParts} ${t('options.overheadParts')}`);
        }
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelOverheadColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelOverheadLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- ${t('options.overheadPartN', { number: index + 1 })}: ${raamtype.name}`);
            }
          }
        });
      }
      if (config.underlight === 'underlight') {
        items.push('- Underlight');
        if (config.underlightParts > 1) {
          items.push(`- ${config.underlightParts} Underlight Parts`);
        }
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          if (raamtype) {
            if (type === 'fixed-panel') {
              let panelInfo = [];
              const panelColorId = config.panelUnderlightColors?.[index];
              if (panelColorId) {
                const panelColorData = getColorById(panelColorId);
                const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
                panelInfo.push(panelColorName);
              } else {
                panelInfo.push('Anthracite Grey (RAL 7016)');
              }
              const panelLineType = config.panelUnderlightLineTypes?.[index] || 'none';
              if (panelLineType === 'horizontal') {
                panelInfo.push('Horizontal Lines');
              } else if (panelLineType === 'vertical') {
                panelInfo.push('Vertical Lines');
              } else {
                panelInfo.push('Plain');
              }
              const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}${panelInfoStr}`);
            } else {
              items.push(`- Underlight Part ${index + 1}: ${raamtype.name}`);
            }
          }
        });
      }
    }
    
    // Wood Joint
    if (config.woodJoint && woodJointOptions && woodJointOptions.length > 0) {
      const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
      if (woodJoint) {
        items.push(`- ${t('options.woodJoint')}: ${woodJoint.name}`);
      }
    }
    
    // Hardware
    if (config.hardware && Array.isArray(config.hardware) && hardware && hardware.length > 0) {
      config.hardware.forEach(hwId => {
        if (hwId) {
          const hw = hardware.find(h => h.id === hwId);
          if (hw) {
            items.push(`- Hardware: ${hw.name}`);
          }
        }
      });
    }
    
    // Accessories
    if (config.accessories && Array.isArray(config.accessories) && accessories && accessories.length > 0) {
      config.accessories.forEach(accId => {
        if (accId) {
          const acc = accessories.find(a => a.id === accId);
          if (acc) {
            items.push(`- Accessory: ${acc.name}`);
          }
        }
      });
    }
    
    // Additional Options
    if (config.screenElectricallyOperated === true) {
      const screenColorData = getColorByRAL(config.screenColor || '7016');
      const screenColorName = screenColorData?.ral ? `RAL ${screenColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.screenElectricallyOperated')} (${screenColorName})`);
    }
    
    if (config.rollerShutterElectricallyOperated === true) {
      const shutterColorData = getColorByRAL(config.rollerShutterColor || '7016');
      const shutterColorName = shutterColorData?.ral ? `RAL ${shutterColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.rollerShutterElectricallyOperated')} (${shutterColorName})`);
    }
    
    if (config.hor === true) {
      const horColorData = getColorByRAL(config.horColor || '7016');
      const horColorName = horColorData?.ral ? `RAL ${horColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push(`- ${t('quote.hor')} (${horColorName})`);
    }
    
    if (config.buoySharing === true) {
      const buoyColorData = getColorByRAL(config.buoySharingColor || '7016');
      const buoyColorName = buoyColorData?.ral ? `RAL ${buoyColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      const mmText = config.buoySharingMm ? `, ${config.buoySharingMm}mm` : '';
      items.push(`- ${t('quote.buoySharing')} (${buoyColorName}${mmText})`);
    }
    
    // Standard included items
    items.push(`- ${t('quote.includeInstallation')}`);
    items.push(`- ${t('quote.standardInteriorFrameFinishing')}`);
    
    return items;
  };

  const LIFTING_ASSISTANCE_FEE = 500;

  // Calculate line subtotal (ex VAT) for a drawing list — no project fees
  const calculateDrawingsSubtotalExVat = (drawings = savedQuotes) => {
    let total = (drawings || []).reduce((sum, quote) => {
      const quantity = quote.config.quantity || 1;
      let price = (quote.pricing?.totalPrice || 0);

      const liftingOn = (quote.clientDetails ?? clientDetails)?.liftingAssistance === true;
      if (liftingOn) {
        price = price + LIFTING_ASSISTANCE_FEE;
      }

      if (isAuthenticated && pricingSettings?.margin_enabled && pricingSettings?.margin_percentage > 0) {
        price = price * (1 + pricingSettings.margin_percentage / 100);
      }

      if (isAuthenticated && pricingSettings?.workers_fixing_enabled && (pricingSettings?.workers_fixing_amount ?? 0) > 0) {
        price = price + (pricingSettings.workers_fixing_amount || 0);
      }

      if (isAuthenticated && pricingSettings?.material_cost_enabled && (pricingSettings?.material_cost_amount ?? 0) > 0) {
        price = price + (pricingSettings.material_cost_amount || 0);
      }

      if (isAuthenticated && pricingSettings?.surcharge_enabled && pricingSettings?.surcharge_amount > 0) {
        const numberOfParts = quote.config.parts || 1;
        const surchargeAmount = pricingSettings.surcharge_amount * numberOfParts;
        price = price + surchargeAmount;
      }

      price = applyDeductionMargin(price, pricingSettings, { isAuthenticated });

      if (isAuthenticated && savedQuoteDiscountEnabled && savedQuoteDiscountPercentage > 0) {
        price = price * (1 - savedQuoteDiscountPercentage / 100);
      }

      return sum + (price * quantity);
    }, 0);

    return total;
  };

  // Calculate total price from all quotes with margin, surcharge, lifting assistance, and optional discount applied
  const calculateSubtotalExVat = (drawings = savedQuotes) => {
    return (
      calculateDrawingsSubtotalExVat(drawings) +
      getQuoteProjectFeesExVat(clientDetails) +
      getQuoteExtraLineItemsExVat(clientDetails)
    );
  };

  const calculateTotalPriceForDrawings = (drawings = savedQuotes, { includeProjectFees = true } = {}) => {
    const subtotal = includeProjectFees
      ? calculateSubtotalExVat(drawings)
      : calculateDrawingsSubtotalExVat(drawings);
    return applyExportPriceAdjustment(subtotal, clientDetails);
  };

  const calculateTotalPrice = () => calculateTotalPriceForDrawings(savedQuotes, { includeProjectFees: true });

  // Calculate total surcharge amount
  const calculateTotalSurcharge = () => {
    if (!isAuthenticated || !pricingSettings?.surcharge_enabled || pricingSettings?.surcharge_amount <= 0) {
      return 0;
    }
    
    return savedQuotes.reduce((sum, quote) => {
      const quantity = quote.config.quantity || 1;
      const numberOfParts = quote.config.parts || 1;
      const surchargePerQuote = pricingSettings.surcharge_amount * numberOfParts;
      return sum + (surchargePerQuote * quantity);
    }, 0);
  };
  
  // Total discount amount when Apply discount is enabled in Saved Quotes
  const calculateTotalDiscount = () => {
    if (!isAuthenticated || !savedQuoteDiscountEnabled || savedQuoteDiscountPercentage <= 0) {
      return 0;
    }
    const totalBeforeDiscount = savedQuotes.reduce((sum, quote) => {
      const quantity = quote.config.quantity || 1;
      let price = (quote.pricing?.totalPrice || 0);
      if (pricingSettings?.margin_enabled && pricingSettings?.margin_percentage > 0) {
        price = price * (1 + pricingSettings.margin_percentage / 100);
      }
      if (pricingSettings?.workers_fixing_enabled && (pricingSettings?.workers_fixing_amount ?? 0) > 0) {
        price = price + (pricingSettings.workers_fixing_amount || 0);
      }
      if (pricingSettings?.material_cost_enabled && (pricingSettings?.material_cost_amount ?? 0) > 0) {
        price = price + (pricingSettings.material_cost_amount || 0);
      }
      if (pricingSettings?.surcharge_enabled && pricingSettings?.surcharge_amount > 0) {
        const numberOfParts = quote.config.parts || 1;
        price = price + (pricingSettings.surcharge_amount * numberOfParts);
      }
      price = applyDeductionMargin(price, pricingSettings, { isAuthenticated });
      return sum + (price * quantity);
    }, 0);
    return totalBeforeDiscount * (savedQuoteDiscountPercentage / 100);
  };

  // Calculate total area from all quotes (m²)
  const subtotalExVatBeforeExportDeduction = calculateSubtotalExVat();
  const totalPrice = calculateTotalPrice();
  const priceAdjustmentState = getExportPriceAdjustmentState(clientDetails);
  const totalPriceAdjustmentInclVat = getQuotePriceAdjustmentInclVat(clientDetails);
  const totalExtraLineItemsInclVat = getQuoteExtraLineItemsInclVat(clientDetails);
  const totalPriceInclVat = totalPrice * BTW_MULTIPLIER_INCL;
  const totalSurcharge = calculateTotalSurcharge();
  const totalDiscount = calculateTotalDiscount();
  // Cover advisor = quote creator (immutable), not whoever is logged in / sending.
  const advisorName = resolveQuoteAdvisorName(
    {
      quoteCreator: activeBackendQuote?.quoteCreator,
      creator_first_name: activeBackendQuote?.creator_first_name,
      creator_last_name: activeBackendQuote?.creator_last_name,
      creator_email: activeBackendQuote?.creator_email,
      // Only for brand-new unsaved quotes with no creator yet.
      fallbackUser: user,
    },
    t('quote.template.defaultAdvisor'),
  );
  const hasQuoteContent = countQuoteDrawings(quoteSections, savedQuotes) > 0;
  const multiSectionMode = isMultiSectionQuote(quoteSections, savedQuotes);

  const exportSections = useMemo(
    () =>
      buildExportSectionList(quoteSections, savedQuotes, {
        currentSectionName: t('summary.unnamedSection'),
      }),
    [quoteSections, savedQuotes, t]
  );

  const allExportDrawings = useMemo(
    () => exportSections.flatMap((section) => section.drawings || []),
    [exportSections]
  );
  const totalArea = getTotalQuotesAreaSqm(
    allExportDrawings.length > 0 ? allExportDrawings : savedQuotes
  );

  const sectionSummaries = useMemo(() => {
    if (!multiSectionMode) return null;
    const sectionsWithDrawings = exportSections.filter((section) => (section.drawings?.length ?? 0) > 0);
    const lastIndex = sectionsWithDrawings.length - 1;
    return sectionsWithDrawings.map((section, sectionIndex) => {
      let sectionTotalExVat = calculateDrawingsSubtotalExVat(section.drawings);
      const isLastSection = sectionIndex === lastIndex;
      if (isLastSection) {
        sectionTotalExVat += getQuoteProjectFeesExVat(clientDetails);
        sectionTotalExVat += getQuoteExtraLineItemsExVat(clientDetails);
        sectionTotalExVat = applyExportPriceAdjustment(sectionTotalExVat, clientDetails);
      }
      return {
        id: section.id,
        name: section.name || t('summary.unnamedSection'),
        totalInclVat: sectionTotalExVat * BTW_MULTIPLIER_INCL,
        areaSqm: getTotalQuotesAreaSqm(section.drawings),
      };
    });
  }, [multiSectionMode, exportSections, clientDetails, t]);

  const useVmkTemplate = asPage;
  const showVmkTemplatePages = useVmkTemplate && includeTemplateInExport;
  // Standalone investment page when template is off but total-page option is on
  const showInvestmentPageOnly =
    useVmkTemplate && includeTotalPageInExport && !includeTemplateInExport;

  const sectionPagePlans = useMemo(
    () => {
      const plans = exportSections
        .filter((section) => (section.drawings?.length ?? 0) > 0)
        .map((section) => ({
          ...section,
          chunks: packQuotesIntoPages(section.drawings, {
            includePricing: includePricingInExport,
            reserveTailSpace: includePricingInExport,
            tailReserveHeight: useVmkTemplate ? 95 : undefined,
          }),
        }));
      return plans;
    },
    [exportSections, includePricingInExport, useVmkTemplate]
  );

  const quoteChunks = useMemo(
    () => sectionPagePlans.flatMap((section) => section.chunks),
    [sectionPagePlans]
  );
  const drawingPageCount = quoteChunks.length;
  const templatePageNumbers = useMemo(() => {
    if (!useVmkTemplate) return null;
    return {
      cover: 1,
      proposal: 2,
      specs: 3,
      investment: getTailPageNumber(drawingPageCount, 0),
      warranty: getTailPageNumber(drawingPageCount, 1),
      process: getTailPageNumber(drawingPageCount, 2),
      sustainability: getTailPageNumber(drawingPageCount, 3),
      agreement: getTailPageNumber(drawingPageCount, 4),
    };
  }, [useVmkTemplate, drawingPageCount]);
  const displayQuote = savedQuotes.length > 0 ? savedQuotes[0] : null;

  // Per-quote line total (same logic as one iteration of calculateTotalPrice) for pricing table rows
  const getQuoteLineTotal = (quote) => {
    const quantity = quote.config?.quantity || 1;
    let price = quote.pricing?.totalPrice || 0;
    const liftingOn = (quote.clientDetails ?? clientDetails)?.liftingAssistance === true;
    if (liftingOn) price += LIFTING_ASSISTANCE_FEE;
    if (isAuthenticated && pricingSettings?.margin_enabled && pricingSettings?.margin_percentage > 0)
      price = price * (1 + pricingSettings.margin_percentage / 100);
    if (isAuthenticated && pricingSettings?.workers_fixing_enabled && (pricingSettings?.workers_fixing_amount ?? 0) > 0)
      price += pricingSettings.workers_fixing_amount || 0;
    if (isAuthenticated && pricingSettings?.material_cost_enabled && (pricingSettings?.material_cost_amount ?? 0) > 0)
      price += pricingSettings.material_cost_amount || 0;
    if (isAuthenticated && pricingSettings?.surcharge_enabled && pricingSettings?.surcharge_amount > 0) {
      const parts = quote.config?.parts || 1;
      price += pricingSettings.surcharge_amount * parts;
    }
    price = applyDeductionMargin(price, pricingSettings, { isAuthenticated });
    if (isAuthenticated && savedQuoteDiscountEnabled && savedQuoteDiscountPercentage > 0)
      price = price * (1 - savedQuoteDiscountPercentage / 100);
    return price * quantity;
  };

  /** Line total incl. 21% BTW — matches configurator Summary "Total" display. */
  const getQuoteLineTotalInclVat = (quote) => getQuoteLineTotal(quote) * BTW_MULTIPLIER_INCL;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const PRICING_BASE_PAGE = 4;
  const drawingPageStart = useVmkTemplate ? VMK_DRAWING_BASE_PAGE : asPage ? 1 : PRICING_BASE_PAGE;
  /** Full quote dialog keeps legacy letterhead; Export page uses 9-page VMK template. */
  const showCoverPage = !asPage && !useVmkTemplate;
  const showLetterAndSpecsPages = !asPage && !useVmkTemplate;
  const guaranteesPageNumber = hasQuoteContent ? PRICING_BASE_PAGE + quoteChunks.length : PRICING_BASE_PAGE;

  const getSvgWithoutBackground = (svgString) => {
    if (!svgString || typeof svgString !== 'string') return '';
    const cleaned = svgString.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, 'fill="#FFF"');
    return enhanceQuoteDrawingSvg(cleaned);
  };

  const vmkPricingSharedProps = {
    quoteNumber,
    clientName: clientDetails.name,
    tableSvgData,
    getSvgWithoutBackground,
    getQuoteLineTotal: getQuoteLineTotalInclVat,
    formatPrice,
    t,
    isEditing: isEditing || dashboardMode || forceEditing,
    // Sent/signed quotes: hide "delete drawing" (cannot overwrite those offers)
    allowRemoveDrawings:
      !quoteIsSigned &&
      !saveAsNewQuote &&
      (canUpdateExistingQuote || !isEditingBackendQuote(activeBackendQuote)),
    isExportingPDF,
    removeQuote,
    setTableSvgData,
    savedQuotes,
    totalPrice,
    subtotalExVatBeforeExportDeduction,
    exportDeductionExVat: 0,
    exportDeductionInclVat: 0,
    deductionLabel: t('quote.exportDeductionLine'),
    deductionLabelExVat: t('quote.exportDeductionLineExVat'),
    showPricing: includePricingInExport,
    showNijBegun: includeNijBegunInExport,
  };

  const vmkPricingPages = hasQuoteContent && (() => {
    let globalPageIndex = 0;
    let globalDrawingIndex = 0;
    let showIntroOnce = true;
    const lastSectionIndex = sectionPagePlans.length - 1;

    return sectionPagePlans.flatMap((section, sectionIndex) => {
      const isLastSection = sectionIndex === lastSectionIndex;
      let sectionTotalExVat = calculateDrawingsSubtotalExVat(section.drawings);
      if (multiSectionMode) {
        if (isLastSection) {
          sectionTotalExVat += getQuoteProjectFeesExVat(clientDetails);
          sectionTotalExVat += getQuoteExtraLineItemsExVat(clientDetails);
          sectionTotalExVat = applyExportPriceAdjustment(sectionTotalExVat, clientDetails);
        }
      } else {
        sectionTotalExVat = calculateTotalPriceForDrawings(section.drawings, { includeProjectFees: true });
      }

      // Price adjustments are silent — never show a separate deduction/addition line
      const sectionTotalsTitle = multiSectionMode && section.name
        ? t('quote.sectionTotalNamed', { name: section.name })
        : null;

      return section.chunks.map((chunk, chunkIndex) => {
        const isLastChunkOfSection = chunkIndex === section.chunks.length - 1;
        const runningGlobalFirstIndex = globalDrawingIndex;
        const page = (
          <VmkQuotePricingPage
            key={`${section.id}-${chunk.map((q) => q.id).join('-') || chunkIndex}`}
            {...vmkPricingSharedProps}
            chunk={chunk}
            globalFirstIndex={runningGlobalFirstIndex}
            compact={chunk.length > 1}
            showIntro={showIntroOnce}
            showSectionHeader={Boolean(section.name) && chunkIndex === 0}
            sectionName={section.name}
            sectionIndex={multiSectionMode ? sectionIndex : null}
            showTotals={isLastChunkOfSection && includePricingInExport}
            showLegal={!useVmkTemplate && isLastSection && isLastChunkOfSection}
            sectionTotalsTitle={sectionTotalsTitle}
            totalPrice={sectionTotalExVat}
            subtotalExVatBeforeExportDeduction={sectionTotalExVat}
            exportDeductionExVat={0}
            exportDeductionInclVat={0}
            pageNumber={
              useVmkTemplate
                ? (includeTemplateInExport ? getDrawingPageNumber(globalPageIndex) : globalPageIndex + 1)
                : drawingPageStart + globalPageIndex
            }
            useBrandedLayout={useVmkTemplate}
          />
        );
        globalPageIndex += 1;
        globalDrawingIndex += chunk.length;
        showIntroOnce = false;
        return page;
      });
    });
  })();

  const getDrawingSvgFromRef = () => {
    if (!drawingBoardRef?.current) return null;
    let svgEl = drawingBoardRef.current.tagName === 'svg' ? drawingBoardRef.current : drawingBoardRef.current.querySelector('svg');
    if (!svgEl && drawingBoardRef.current.children?.length) {
      const children = Array.from(drawingBoardRef.current.children);
      svgEl = children.find(c => c.tagName === 'svg') || children.find(c => c.querySelector('svg'))?.querySelector('svg');
    }
    if (!svgEl) return null;
    const clone = svgEl.cloneNode(true);
    let html = clone.outerHTML;
    html = html.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, 'fill="#FFF"');
    return html;
  };

  const finalPrices = useMemo(() => {
    const quote = savedQuotes.length > 0 ? savedQuotes[0] : null;
    if (!quote) return { totalPrice: 0, surchargeAmount: 0, discountAmount: 0, originalPrice: 0 };
    const qty = quote.config?.quantity || 1;
    let price = quote.pricing?.totalPrice || 0;
    if ((quote.clientDetails ?? clientDetails)?.liftingAssistance === true) price += LIFTING_ASSISTANCE_FEE;
    let priceWithMargin = price;
    if (isAuthenticated && pricingSettings?.margin_enabled && pricingSettings?.margin_percentage > 0) {
      priceWithMargin = price * (1 + pricingSettings.margin_percentage / 100);
      price = priceWithMargin;
    }
    if (isAuthenticated && pricingSettings?.workers_fixing_enabled && (pricingSettings?.workers_fixing_amount ?? 0) > 0)
      price += pricingSettings.workers_fixing_amount || 0;
    if (isAuthenticated && pricingSettings?.material_cost_enabled && (pricingSettings?.material_cost_amount ?? 0) > 0)
      price += pricingSettings.material_cost_amount || 0;
    let surchargeAmount = 0;
    if (isAuthenticated && pricingSettings?.surcharge_enabled && pricingSettings?.surcharge_amount > 0) {
      const parts = quote.config?.parts || 1;
      surchargeAmount = pricingSettings.surcharge_amount * parts;
      price += surchargeAmount;
    }
    price = applyDeductionMargin(price, pricingSettings, { isAuthenticated });
    let discountAmount = 0;
    if (isAuthenticated && savedQuoteDiscountEnabled && savedQuoteDiscountPercentage > 0) {
      discountAmount = price * (savedQuoteDiscountPercentage / 100);
      price -= discountAmount;
    }
    return { totalPrice: price, surchargeAmount, discountAmount, originalPrice: priceWithMargin };
  }, [savedQuotes, clientDetails.liftingAssistance, isAuthenticated, pricingSettings, savedQuoteDiscountEnabled, savedQuoteDiscountPercentage]);

  const updatePage3TableWithDrawingAndDetails = () => {
    if (savedQuotes.length === 0) return;
    const quote = savedQuotes[0];
    const svgData = getDrawingSvgFromRef();
    const updates = { clientDetails: { ...clientDetails } };
    if (svgData != null) {
      updates.svgData = svgData;
      setTableSvgData(svgData);
    } else {
      setTableSvgData(displayQuote?.svgData ?? null);
    }
    updateQuote(quote.id, updates);
  };

  const handleSavePreview = () => {
    if (savedQuotes.length === 0) {
      setIsEditing(false);
      setIsSaved(true);
      return;
    }
    const quote = savedQuotes[0];
    const clientDetailsSnapshot = { ...clientDetails };
    const captureAndUpdate = () => {
      const svgData = getDrawingSvgFromRef();
      savedQuotes.forEach((q, index) => {
        const updates = { clientDetails: clientDetailsSnapshot };
        if (index === 0 && svgData != null) {
          updates.svgData = svgData;
          setTableSvgData(svgData);
        }
        updateQuote(q.id, updates);
      });
      if (svgData == null) {
        setTableSvgData(quote.svgData ?? null);
      }
      if (isEditingBackendQuote(activeBackendQuote)) {
        patchActiveBackendQuote({ clientDetails: clientDetailsSnapshot });
      }
      setIsEditing(false);
      setIsSaved(true);
      requestAnimationFrame(() => {
        actionButtonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    };
    requestAnimationFrame(captureAndUpdate);
  };

  const handleSaveToMyQuotes = async () => {
    if (!isAuthenticated || !user) {
      alert('Please login to save quotes to My Quotes');
      return;
    }

    if (quoteIsSigned) {
      alert(t('quote.cannotUpdateSignedQuote') || 'Signed quotes cannot be changed.');
      return;
    }

    if (!asPage && (isEditing || !isSaved)) {
      alert('Please save the quote details before saving to My Quotes');
      return;
    }

    if (!hasQuoteContent) {
      alert('No quotes to save');
      return;
    }

    updatePage3TableWithDrawingAndDetails();

    try {
      const totalAmount = computeStoredQuoteTotalInclVat(savedQuotes, clientDetails, quoteSections);

      const contactIdForApi = resolveQuoteContactId(clientDetails);

      // Never copy a prior signature onto a forked / new quote
      const signatureFields = (() => {
        if (!canUpdateExistingQuote) return {};
        const signed = activeBackendQuote?.signedAgreement;
        if (!signed?.signature && !signed?.date && !signed?.name) return {};
        return {
          signature: signed.signature || undefined,
          signed_by_name: signed.name || undefined,
          signed_at: signed.date || undefined,
        };
      })();

      const quotePayload = {
        quote_data: withQuoteCreator(
          {
            ...serializeQuoteDrawingsPayload(savedQuotes, quoteSections),
            clientDetails,
            exportOptions: {
              includePricingInExport,
              includeTemplateInExport,
              includeNijBegunInExport,
              includeTotalPageInExport: includeTemplateInExport || includeTotalPageInExport,
            },
            ...signatureFields,
          },
          user,
          preserveCreatorFromBackendQuote(activeBackendQuote),
        ),
        status: 'draft',
        total_amount: totalAmount,
        valid_until:
          (canUpdateExistingQuote && activeBackendQuote?.valid_until) ||
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        ...(contactIdForApi ? { contact_id: contactIdForApi } : {}),
      };

      if (canUpdateExistingQuote) {
        await axios.put(`${API_URL}/api/quotes/${activeBackendQuote.id}`, quotePayload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        patchActiveBackendQuote({ clientDetails, quoteNumber, clientContactOverride: false });
        alert(`Successfully updated quote with ${countQuoteDrawings(quoteSections, savedQuotes)} drawing(s)!`);
        return;
      }

      // Sent (or other non-draft) quotes: always create a new offer
      const newQuoteNumber = saveAsNewQuote ? generateQuoteNumber() : (quoteNumber || generateQuoteNumber());
      const response = await axios.post(
        `${API_URL}/api/quotes`,
        {
          quote_number: newQuoteNumber,
          ...quotePayload,
          currency: 'EUR',
          company_id: user.company_id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );

      if (response.data) {
        setQuoteNumber(response.data.quote_number || newQuoteNumber);
        loadBackendQuoteSession(response.data);
        if (dashboardMode && response.data.id) {
          navigate(`/dashboard/quotes/${response.data.id}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error saving quotes:', error);
      const detail = formatApiErrorDetail(error.response?.data?.detail);
      alert(detail || 'Failed to save quotes. Please try again.');
    }
  };

  const handleSendQuoteByEmail = async (recipientEmail) => {
    if (!isAdmin) return;
    if (sendingQuoteEmailRef.current) return;
    const trimmedEmail = (recipientEmail || '').trim();
    if (!trimmedEmail) return;
    if (!isValidEmail(trimmedEmail)) {
      setSendEmailError(t('quote.invalidEmail'));
      return;
    }
    setSendEmailError('');
    if (!hasQuoteContent) {
      alert(t('quote.pleaseSaveBeforeExport') || 'Please save at least one drawing first.');
      return;
    }
    if (quoteIsSigned) {
      setSendEmailError(t('quote.cannotUpdateSignedQuote') || 'Signed quotes cannot be changed.');
      return;
    }
    // Draft: update in place. Sent quote being edited: new number so the original stays.
    // Brand-new export: keep the number already shown (retry must not create a second card).
    const { quoteId: existingQuoteId, quoteNumber: effectiveQuoteNumber } =
      resolveQuoteSendPersistence(activeBackendQuote, quoteNumber);
    setQuoteNumber(effectiveQuoteNumber);
    sendingQuoteEmailRef.current = true;
    setSendingQuoteEmail(true);
    try {
      const subtotalVal = totalPrice;
      const vatVal = subtotalVal * 0.21;
      const totalVal = subtotalVal + vatVal;
      const drawings = savedQuotes.map((quote) => {
        const qty = quote.config?.quantity || 1;
        const lineTotal = getQuoteLineTotal(quote);
        return {
          config: quote.config,
          pricing: {
            ...(quote.pricing || {}),
            quantity: qty,
            totalPrice: lineTotal / qty,
            subtotal: lineTotal,
            vat: lineTotal * 0.21,
            totalPriceWithVAT: lineTotal * 1.21,
          },
          dimensions: quote.dimensions || { width: quote.config?.width, height: quote.config?.height, area: quote.dimensions?.area },
          svgData: quote.svgData || '',
          quantity: qty,
        };
      });
      const items = savedQuotes.map((quote) => {
        const qty = quote.config?.quantity || 1;
        const lineTotal = getQuoteLineTotal(quote);
        return {
          title: `${qty} x ${t('quote.plasticFrames')}${(quote.config?.parts || 1) > 1 ? ` (${t('quote.multipleParts')})` : ''}`,
          price: lineTotal,
          included: getDescriptionItemsForQuote(quote),
        };
      });
      const clientDetailsForPayload = {
        ...clientDetails,
        streetAddress: clientDetails.address || clientDetails.streetAddress,
      };
      const quotePayload = {
        to_email: trimmedEmail,
        client_name: clientDetails.name || '',
        quote_number: effectiveQuoteNumber,
        language,
        ...(existingQuoteId ? { quote_id: existingQuoteId } : {}),
        quote_data: withQuoteCreator(
          {
            ...serializeQuoteDrawingsPayload(savedQuotes, quoteSections),
            clientDetails: clientDetailsForPayload,
            isMultipleDrawings: countQuoteDrawings(quoteSections, savedQuotes) > 1,
            recipient_name: clientDetails.name || 'Customer',
            sent_to_email: trimmedEmail,
            recipient_email: trimmedEmail,
            quote_number: effectiveQuoteNumber,
            expires_in_days: 7,
            language,
            items,
            subtotal: subtotalVal,
            vat: vatVal,
            total: totalVal,
            exportOptions: {
              includePricingInExport,
              includeTemplateInExport,
              includeNijBegunInExport,
              includeTotalPageInExport: includeTemplateInExport || includeTotalPageInExport,
            },
          },
          user,
          preserveCreatorFromBackendQuote(activeBackendQuote),
        ),
      };
      const res = await axios.post(`${API_URL}/api/send-quote-email`, quotePayload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (res.data?.success) {
        const sentNumber = res.data.quoteNumber || effectiveQuoteNumber;
        const persistedId = res.data.quoteId || existingQuoteId;
        setQuoteNumber(sentNumber);
        // Send always persists to My Quotes on the backend — bind the session so the offer is linked
        if (persistedId) {
          try {
            const qRes = await axios.get(`${API_URL}/api/quotes/${persistedId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            });
            if (qRes.data) {
              loadBackendQuoteSession(qRes.data);
            } else {
              patchActiveBackendQuote({
                id: persistedId,
                quoteNumber: sentNumber,
                status: 'sent',
              });
            }
          } catch {
            patchActiveBackendQuote({
              id: persistedId,
              quoteNumber: sentNumber,
              status: 'sent',
            });
          }
        }
        setSignLaterEmailSent(true);
        setSignLaterEmail(trimmedEmail);
        setSendEmailDialogSuccess(true);
        setSendEmailError('');
      } else {
        const msg = t('quote.emailSentError') || 'Failed to send email: unexpected server response';
        setSendEmailError(msg);
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      const msg = detail
        ? `${t('quote.emailSentError') || 'Failed to send email.'} ${detail}`
        : (t('quote.emailSentError') || 'Failed to send email: ' + err.message);
      setSendEmailError(msg);
    } finally {
      sendingQuoteEmailRef.current = false;
      setSendingQuoteEmail(false);
    }
  };

  const handleExportPDF = async () => {
    if (!quoteRef.current) return;

    try {
      setIsExportingPDF(true);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      await new Promise((resolve) => setTimeout(resolve, 150));

      quoteRef.current.classList.add('pdf-exporting');

      // Let layout unscale, then remasure overlays before capture
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      window.dispatchEvent(new CustomEvent('vmk-remasure-dim-overlays'));
      await new Promise((resolve) => setTimeout(resolve, 80));

      const pageSelector = '.quote-pdf-page';
      const restoreFooters = renumberQuotePageFooters(quoteRef.current, pageSelector);

      let pageCount;
      try {
        pageCount = await exportPdfPages(quoteRef.current, `${quoteNumber || 'SavedQuote'}.pdf`, {
          scale: 2.5,
          marginMm: 0,
          pageSelector,
        });
      } finally {
        restoreFooters();
        quoteRef.current?.classList.remove('pdf-exporting');
      }

      setIsExportingPDF(false);
      alert(`Successfully exported your quote as a ${pageCount}-page PDF.`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF.');
      setIsExportingPDF(false);
    }
  };

  const handlePrint = async () => {
    if (!quoteRef.current) return;

    const pageSelector = '.quote-pdf-page';

    try {
      setIsPrinting(true);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      await new Promise((resolve) => setTimeout(resolve, 150));

      quoteRef.current.classList.add('pdf-exporting');
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      window.dispatchEvent(new CustomEvent('vmk-remasure-dim-overlays'));
      await new Promise((resolve) => setTimeout(resolve, 80));
      const restoreFooters = renumberQuotePageFooters(quoteRef.current, pageSelector);

      try {
        await printQuotePages(quoteRef.current, { scale: 2.5, pageSelector });
      } finally {
        restoreFooters();
        quoteRef.current?.classList.remove('pdf-exporting');
      }
    } catch (error) {
      console.error('Error preparing print:', error);
      alert('An error occurred while preparing the print preview.');
    } finally {
      setIsPrinting(false);
    }
  };

  const exportActionsDisabled = asPage
    ? !hasQuoteContent
    : isEditing || !isSaved || !hasQuoteContent;

  const actionButtons = (
    <div className={asPage ? 'flex flex-col gap-2' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-2'}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handlePrint}
        disabled={!hasQuoteContent || isPrinting || isExportingPDF}
        className="h-10 w-full justify-center border-slate-300 text-slate-700 hover:bg-white"
      >
        <Printer className="mr-2 h-4 w-4 shrink-0" />
        {isPrinting ? (t('quote.printing') || 'Preparing print…') : t('quote.print')}
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={handleExportPDF}
        disabled={exportActionsDisabled || isExportingPDF || isPrinting}
        className="h-10 w-full justify-center bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
      >
        <FileDown className="mr-2 h-4 w-4 shrink-0" />
        {isExportingPDF ? t('quote.exportingPDF') : t('quote.exportAsPDF')}
      </Button>
      {isAuthenticated && isAdmin && !quoteIsSigned && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const email = (signLaterEmail && signLaterEmail.trim())
              ? signLaterEmail.trim()
              : (clientDetails.email || '');
            setSendEmailTo(email);
            setSendEmailDialogSuccess(false);
            setShowSendEmailDialog(true);
          }}
          disabled={exportActionsDisabled || sendingQuoteEmail}
          className="h-10 w-full justify-center border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
        >
          <Mail className="mr-2 h-4 w-4 shrink-0" />
          {sendingQuoteEmail ? t('quote.sending') : t('quote.sendQuoteByEmail')}
        </Button>
      )}
      {isAuthenticated && !quoteIsSigned && (
        <Button
          type="button"
          size="sm"
          onClick={handleSaveToMyQuotes}
          disabled={exportActionsDisabled}
          className={`h-10 w-full justify-center bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm ${asPage ? '' : 'sm:col-span-2 xl:col-span-1'}`}
        >
          <Save className="mr-2 h-4 w-4 shrink-0" />
          {canUpdateExistingQuote
            ? (t('quote.updateMyQuote') || 'Update quote')
            : saveAsNewQuote
              ? (t('quote.saveAsNewQuote') || 'Save as new quote')
              : (t('quote.saveToMyQuotes') || 'Save to My Quotes')}
        </Button>
      )}
    </div>
  );

  const quoteTools = ((isEditing || dashboardMode) && savedQuotes.length > 0 && !dashboardMode) || (dashboardMode || forceEditing) ? (
    <div className={`flex gap-2 ${asPage ? 'flex-col' : 'flex-wrap items-center'}`}>
      {(isEditing || dashboardMode) && savedQuotes.length > 0 && !dashboardMode && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-9 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 ${asPage ? 'w-full justify-center' : ''}`}
          onClick={() => {
            if (window.confirm(t('savedQuotes.clearAllConfirm') || 'Clear all saved quotes?')) {
              clearAllQuotes();
              setTableSvgData(null);
            }
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('savedQuotes.clearAll') || 'Clear all saved quotes'}
        </Button>
      )}
      {(dashboardMode || forceEditing) && !quoteIsSigned && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-9 ${asPage ? 'w-full justify-center' : ''}`}
          onClick={() => {
            const selectedContact = buildSelectedContactFromClientDetails(clientDetails);
            if (selectedContact) {
              persistActiveContactForConfigurator(selectedContact, user?.id);
            }
            navigate('/configurator-select', {
              state: {
                returnTo: dashboardMode ? `/dashboard/quotes/${activeBackendQuote?.id}` : '/export-quote',
                preserveQuoteSession: true,
                ...(selectedContact ? { selectedContact } : {}),
              },
            });
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('dashboard.addDrawing') || 'Add drawing'}
        </Button>
      )}
    </div>
  ) : null;

  const exportOptionsPanel = asPage ? (
    <div className={asPage ? 'space-y-4' : 'rounded-lg border border-slate-200 bg-white shadow-sm divide-y divide-slate-100'}>
      <div className={asPage ? '' : 'px-4 py-3.5'}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
          {t('quote.exportSettings')}
        </p>
        <div className={`flex gap-2.5 ${asPage ? 'flex-col' : 'flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2.5'}`}>
          <label htmlFor="export-include-pricing" className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="export-include-pricing"
              checked={includePricingInExport}
              onCheckedChange={(checked) => setIncludePricingInExport(checked === true)}
              className="h-4 w-4"
            />
            <span className="text-sm text-slate-700 leading-snug">
              {t('summary.includePricingInExport')}
            </span>
          </label>
          <label htmlFor="export-include-template" className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="export-include-template"
              checked={includeTemplateInExport}
              onCheckedChange={(checked) => setIncludeTemplateInExport(checked === true)}
              className="h-4 w-4"
            />
            <span className="text-sm text-slate-700 leading-snug">
              {t('summary.includeTemplateInExport')}
            </span>
          </label>
          <label
            htmlFor="export-include-total-page"
            className={`flex items-center gap-2 ${includeTemplateInExport ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
          >
            <Checkbox
              id="export-include-total-page"
              checked={includeTemplateInExport || includeTotalPageInExport}
              disabled={includeTemplateInExport}
              onCheckedChange={(checked) => {
                if (!includeTemplateInExport) {
                  setIncludeTotalPageInExport(checked === true);
                }
              }}
              className="h-4 w-4"
            />
            <span className="text-sm text-slate-700 leading-snug">
              {t('summary.includeTotalPageInExport')}
            </span>
          </label>
          <label htmlFor="export-include-nij-begun" className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="export-include-nij-begun"
              checked={includeNijBegunInExport}
              onCheckedChange={(checked) => setIncludeNijBegunInExport(checked === true)}
              className="h-4 w-4"
            />
            <span className="text-sm text-slate-700 leading-snug">
              {t('summary.includeNijBegunInExport')}
            </span>
          </label>
        </div>
        <div className="mt-3">
          <YesNoToggle
            label={t('quote.subjectToFinance')}
            value={clientDetails.subjectToFinance === true}
            onChange={(value) => setClientDetails({ ...clientDetails, subjectToFinance: value })}
            yesLabel={t('common.yes')}
            noLabel={t('common.no')}
            disabled={quoteIsSigned}
          />
        </div>
      </div>

      <div className={asPage ? 'pt-4 border-t border-slate-100' : 'px-4 py-3.5'}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
          {t('quote.extraItems')}
        </p>
        <div className="flex flex-col gap-3">
          {(clientDetails.extraLineItems || []).map((item, index) => (
            <div
              key={item.id || index}
              className="rounded-md border border-slate-200 bg-slate-50/80 p-2.5 space-y-2"
            >
              <Input
                type="text"
                value={item.name || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setClientDetails((prev) => ({
                    ...prev,
                    extraLineItems: (prev.extraLineItems || []).map((row) =>
                      row.id === item.id ? { ...row, name: value } : row
                    ),
                  }));
                }}
                placeholder={t('quote.extraItemsDesc')}
                className="h-9 text-sm"
                aria-label={t('quote.extraItemsDesc')}
              />
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={
                      Object.prototype.hasOwnProperty.call(extraItemPriceDrafts, item.id)
                        ? extraItemPriceDrafts[item.id]
                        : item.price === 0 || item.price == null
                          ? ''
                          : String(item.price)
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      setExtraItemPriceDrafts((drafts) => ({ ...drafts, [item.id]: raw }));
                    }}
                    onBlur={(e) => {
                      const parsed = parseAdditionalItemPrice(e.target.value);
                      setClientDetails((prev) => ({
                        ...prev,
                        extraLineItems: (prev.extraLineItems || []).map((row) =>
                          row.id === item.id ? { ...row, price: parsed } : row
                        ),
                      }));
                      setExtraItemPriceDrafts((drafts) => {
                        const next = { ...drafts };
                        delete next[item.id];
                        return next;
                      });
                    }}
                    placeholder={t('quote.extraItemsPrice')}
                    className="h-9 w-full pl-8 text-sm tabular-nums"
                    aria-label={t('quote.extraItemsPrice')}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setClientDetails((prev) => ({
                      ...prev,
                      extraLineItems: (prev.extraLineItems || []).filter((row) => row.id !== item.id),
                    }));
                    setExtraItemPriceDrafts((drafts) => {
                      const next = { ...drafts };
                      delete next[item.id];
                      return next;
                    });
                  }}
                  aria-label={t('quote.extraItemsRemove')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full justify-center"
            onClick={() => {
              setClientDetails((prev) => ({
                ...prev,
                extraLineItems: [
                  ...(prev.extraLineItems || []),
                  createAdditionalItem((prev.extraLineItems || []).length + 1),
                ],
              }));
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('quote.extraItemsAdd')}
          </Button>

          {totalExtraLineItemsInclVat > 0 && (
            <p className="text-xs text-slate-500 leading-snug">
              {t('quote.extraItemsHint')}{' '}
              <span className="font-medium text-slate-700 tabular-nums">
                +{formatPrice(totalExtraLineItemsInclVat)}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className={asPage ? 'pt-4 border-t border-slate-100' : 'px-4 py-3.5'}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
          {t('quote.priceAdjustment')}
        </p>
        <div className="flex flex-col gap-3">
          <label htmlFor="export-price-adj-enabled" className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="export-price-adj-enabled"
              checked={priceAdjustmentState.enabled}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                const mode = clientDetails.exportPriceAdjustmentMode === 'add' ? 'add' : 'subtract';
                setClientDetails({
                  ...clientDetails,
                  exportPriceAdjustmentEnabled: enabled,
                  exportPriceAdjustmentMode: mode,
                  exportPriceAdjustmentAmount: enabled
                    ? clampExportPriceAdjustmentEur(
                        clientDetails.exportPriceAdjustmentAmount ?? clientDetails.exportDeductionAmount,
                        mode,
                        { isAdmin }
                      )
                    : 0,
                  // Clear legacy deduction so state stays consistent
                  exportDeductionEnabled: false,
                  exportDeductionAmount: 0,
                });
              }}
              className="h-4 w-4"
            />
            <span className="text-sm text-slate-700">
              {t('quote.priceAdjustmentApply')}
            </span>
          </label>

          {priceAdjustmentState.enabled && (
            <div className="flex flex-col gap-2 pl-6">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setClientDetails({
                      ...clientDetails,
                      exportPriceAdjustmentMode: 'add',
                      exportPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        clientDetails.exportPriceAdjustmentAmount,
                        'add',
                        { isAdmin }
                      ),
                    });
                  }}
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${
                    priceAdjustmentState.mode === 'add'
                      ? 'border-green-600 bg-green-50 text-green-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t('quote.priceAdjustmentAdd')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setClientDetails({
                      ...clientDetails,
                      exportPriceAdjustmentMode: 'subtract',
                      exportPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        clientDetails.exportPriceAdjustmentAmount,
                        'subtract',
                        { isAdmin }
                      ),
                    });
                  }}
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${
                    priceAdjustmentState.mode === 'subtract'
                      ? 'border-orange-600 bg-orange-50 text-orange-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t('quote.priceAdjustmentSubtract')}
                </button>
              </div>

              <div className="relative flex-1 min-w-0">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
                <Input
                  id="export-price-adj-amount"
                  type="number"
                  min="0"
                  max={
                    priceAdjustmentState.mode === 'subtract' && !isAdmin
                      ? MAX_USER_PRICE_REDUCTION_EUR
                      : undefined
                  }
                  step="0.01"
                  inputMode="decimal"
                  value={clientDetails.exportPriceAdjustmentAmount ?? 0}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setClientDetails({ ...clientDetails, exportPriceAdjustmentAmount: '' });
                      return;
                    }
                    // Allow typing decimals (22.33, .33); normalize on blur
                    if (/^\d*[.,]?\d*$/.test(raw.trim())) {
                      setClientDetails({
                        ...clientDetails,
                        exportPriceAdjustmentAmount: raw,
                      });
                    }
                  }}
                  onBlur={() => {
                    setClientDetails({
                      ...clientDetails,
                      exportPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        parseExportPriceAdjustmentAmount(clientDetails.exportPriceAdjustmentAmount),
                        priceAdjustmentState.mode,
                        { isAdmin }
                      ),
                    });
                  }}
                  className="h-9 w-full pl-8 text-sm tabular-nums"
                  aria-label={t('quote.priceAdjustmentAmount')}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-400">
                  {priceAdjustmentState.mode === 'subtract' && !isAdmin
                    ? t('quote.priceAdjustmentMaxReduce', { max: MAX_USER_PRICE_REDUCTION_EUR })
                    : priceAdjustmentState.mode === 'add'
                      ? t('quote.priceAdjustmentAddHint')
                      : t('quote.priceAdjustmentAdminHint')}
                </span>
                {totalPriceAdjustmentInclVat !== 0 && (
                  <span
                    className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums ${
                      totalPriceAdjustmentInclVat > 0
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-orange-200 bg-orange-50 text-orange-800'
                    }`}
                  >
                    {totalPriceAdjustmentInclVat > 0 ? '+' : '−'}
                    {formatPrice(Math.abs(totalPriceAdjustmentInclVat))}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const quoteActionBar = asPage ? (
    <div ref={actionButtonsRef} className="print:hidden p-4 sm:p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {t('quote.actions') || 'Actions'}
      </h3>
      {hasQuoteContent ? (
        <p className="text-sm font-semibold text-slate-800 mb-4 tabular-nums">
          {t('summary.totalDrawings', {
            count: countQuoteDrawings(quoteSections, savedQuotes),
          })}
        </p>
      ) : (
        <div className="mb-4" />
      )}
      <div className="space-y-5">
        {exportOptionsPanel}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
            {t('quote.exportAndSave')}
          </p>
          {actionButtons}
        </div>
        {quoteTools ? (
          <div className="pt-4 border-t border-slate-100">
            {quoteTools}
          </div>
        ) : null}
      </div>
    </div>
  ) : (
    <div
      ref={actionButtonsRef}
      className="print:hidden border-t border-slate-200 bg-slate-50/90 mt-8"
    >
      <div className="px-2 py-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t('quote.actions') || 'Actions'}
          </h3>
          {hasQuoteContent ? (
            <span className="text-sm font-semibold text-slate-800 tabular-nums">
              {t('summary.totalDrawings', {
                count: countQuoteDrawings(quoteSections, savedQuotes),
              })}
            </span>
          ) : null}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_17.5rem] gap-6 items-start">
          <div className="space-y-3 min-w-0">
            {quoteTools}
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3 hidden xl:block">
              {t('quote.exportAndSave')}
            </p>
            {actionButtons}
          </div>
        </div>
      </div>
    </div>
  );

  const sendEmailDialog = isAdmin ? (
        <Dialog open={showSendEmailDialog} onOpenChange={(dialogOpen) => { setShowSendEmailDialog(dialogOpen); if (!dialogOpen) { setSendEmailDialogSuccess(false); setSendEmailError(''); } }}>
          <DialogContent className="max-w-sm" aria-describedby="send-quote-email-desc">
            <DialogHeader>
              <DialogTitle>{t('quote.sendQuoteByEmail')}</DialogTitle>
            </DialogHeader>
            <p id="send-quote-email-desc" className="sr-only">{t('quote.enterEmailToSendQuote')}</p>
            <div className="space-y-4 pt-2">
              {sendEmailDialogSuccess ? (
                <>
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <Check className="w-8 h-8 text-green-600 stroke-[2.5]" />
                    </div>
                    <p className="text-sm text-gray-700 text-center">{t('quote.emailSentSuccess')}</p>
                  </div>
                  <div className="flex justify-center">
                    <Button onClick={() => { setShowSendEmailDialog(false); setSendEmailDialogSuccess(false); }} className="bg-green-600 hover:bg-green-700">OK</Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-sm text-gray-700">{t('quote.enterEmailToSendQuote')}</Label>
                    <Input
                      type="email"
                      placeholder={t('quote.email') || 'Email'}
                      value={sendEmailTo}
                      onChange={(e) => { const v = e.target.value; setSendEmailTo(v); setSignLaterEmail(v); setSendEmailError(''); }}
                      className="mt-2"
                    />
                    {sendEmailError ? (
                      <p className="mt-2 text-sm text-red-600">{sendEmailError}</p>
                    ) : null}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowSendEmailDialog(false)} disabled={sendingQuoteEmail}>{t('common.cancel')}</Button>
                    <Button type="button" className="bg-blue-600 hover:bg-blue-700" disabled={!sendEmailTo.trim() || sendingQuoteEmail || !isValidEmail(sendEmailTo.trim())} onClick={() => handleSendQuoteByEmail(sendEmailTo)}>
                      {sendingQuoteEmail ? t('quote.sending') : t('quote.sendQuoteByEmail')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
  ) : null;

  const quotePagesBody = (
        <>
          {showCoverPage && (
          <>
          {/* Page 1: Cover (client/contact) — omitted on Export Quote */}
          <div className="quote-pdf-page mb-4 quote-letterhead-page">
          <div
            className="quote-pdf-page-inner border-2 border-black box-border flex flex-col quote-cover-box"
          >
            {/* Cover page header – VMK Kozijnen + address / Offerte + Datum */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-center gap-0 mb-2">
                    <span className="text-black font-bold text-xl">VMK Kozijnen</span>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">KUNSTSTOF & ALUMINIUM / KOZIJNEN / DEUREN / DAKKAPELLEN</p>
                  <p className="text-xs text-gray-600">De duurzame zekerheid voor kunststof kozijnen.</p>
                </div>
                <div className="text-right flex flex-col items-end">
                  <div className="text-sm text-gray-700 mb-3">
                    <p>Havenkade 64</p>
                    <p>7553 GN Hengelo (ov)</p>
                    <p>Tel: +31 (0)6 33 03 30 82</p>
                    <p>Email: Info@vmkkozijnen.nl</p>
                  </div>
                  <p className="text-sm text-black">{t('quote.date')}: {(() => {
                    const d = new Date();
                    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                  })()}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
                <span><strong>{t('quote.quoteLabel')}:</strong> {formatQuoteDisplayReference(quoteNumber, formatQuoteClientDisplayName(clientDetails) || clientDetails.name)}</span>
                <span><strong>{t('quote.validUntil')}:</strong> {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}</span>
              </div>
            </div>

            {/* Contact Selector - Only show when editing, hide during PDF export */}
          {isEditing && !isExportingPDF && isAuthenticated && (
            <div className="mb-2">
              <Label className="text-xs mb-1 block">{t('configurator.selectContact')}</Label>
              <ContactSearchPicker
                size="sm"
                selectedContact={
                  clientDetails.contactId || clientDetails.name
                    ? {
                        id: clientDetails.contactId,
                        name: clientDetails.name,
                      }
                    : null
                }
                onSelect={handleContactSelect}
                buttonClassName="h-8 text-xs"
              />
            </div>
          )}

          {/* Client Address Section - show list (like Quote) when exporting PDF, else edit/display by isEditing */}
          <div className="mb-4 mt-2 print:mt-16">
            {(isEditing && !isExportingPDF) ? (
              <div className="space-y-1">
                <Label htmlFor="clientName" className="text-xs">{t('quote.clientName')} *</Label>
                <Input
                  id="clientName"
                  value={clientDetails.name || ''}
                  onChange={(e) => setClientDetails({ ...clientDetails, name: e.target.value })}
                  placeholder={t('quote.clientName')}
                  className="h-7 text-xs"
                />
                <Label htmlFor="clientAddress" className="text-xs">{t('quote.clientAddress')} *</Label>
                <Input
                  id="clientAddress"
                  value={clientDetails.address || ''}
                  onChange={(e) => setClientDetails({...clientDetails, address: e.target.value})}
                  placeholder={t('quote.streetAddress')}
                  className="h-7 text-xs"
                />
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPostalCode" className="text-xs">{t('quote.postalCode')} *</Label>
                    <Input
                      id="clientPostalCode"
                      value={clientDetails.postalCode || ''}
                      onChange={(e) => setClientDetails({...clientDetails, postalCode: e.target.value})}
                      placeholder="1234 AB"
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientCity" className="text-xs">{t('quote.city')} *</Label>
                    <Input
                      id="clientCity"
                      value={clientDetails.city || ''}
                      onChange={(e) => setClientDetails({...clientDetails, city: e.target.value})}
                      placeholder={t('quote.city')}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label htmlFor="clientPhone" className="text-xs">{t('quote.phone')}</Label>
                    <Input
                      id="clientPhone"
                      type="tel"
                      value={clientDetails.phone || ''}
                      onChange={(e) => setClientDetails({...clientDetails, phone: e.target.value})}
                      placeholder={t('quote.phone')}
                      className="h-7 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clientEmail" className="text-xs">{t('quote.email')}</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      value={clientDetails.email || ''}
                      onChange={(e) => setClientDetails({...clientDetails, email: e.target.value})}
                      placeholder={t('quote.email')}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-left">
                <ul className="list-disc block max-w-md ml-0 space-y-2 my-2 pl-5 text-sm quote-client-details">
                  {clientDetails.name && (
                    <li><span className="font-normal text-gray-900 inline-block w-52">{t('quote.clientName') || 'Name'}</span><span className="font-normal text-gray-900"> : {formatQuoteClientDisplayName(clientDetails) || formatPersonName(clientDetails.name)}</span></li>
                  )}
                  {clientDetails.address && (
                    <li><span className="font-normal text-gray-900 inline-block w-52">{t('quote.streetAddress') || 'Address'}</span><span className="font-normal text-gray-900"> : {clientDetails.address}</span></li>
                  )}
                  {(clientDetails.postalCode || clientDetails.city) && (
                    <li><span className="font-normal text-gray-900 inline-block w-52">{t('quote.postalCode') || 'Postcode'}</span><span className="font-normal text-gray-900"> : {clientDetails.postalCode} {clientDetails.city}</span></li>
                  )}
                  {clientDetails.phone && (
                    <li><span className="font-normal text-gray-900 inline-block w-52">{t('quote.phone')}</span><span className="font-normal text-gray-900"> : {clientDetails.phone}</span></li>
                  )}
                  {clientDetails.email && (
                    <li><span className="font-normal text-gray-900 inline-block w-52">{t('quote.email')}</span><span className="font-normal text-gray-900"> : {clientDetails.email}</span></li>
                  )}
                </ul>
                {!(clientDetails.name || clientDetails.address || clientDetails.postalCode || clientDetails.city || clientDetails.phone || clientDetails.email) && (
                  <p className="text-gray-400 italic text-sm">{t('quote.clientAddress')}</p>
                )}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-700 print:hidden text-xs mt-2 ml-0"
                    title={t('quote.editAddress')}
                  >
                    <Pencil className="w-3 h-3" />
                    <span>{t('quote.editAddress')}</span>
                  </button>
                )}
              </div>
            )}
          </div>

            {/* Cover image - inside border */}
            <div className="my-6">
              <img
                src={process.env.PUBLIC_URL + '/images/front.png'}
                alt=""
                className="block w-full max-w-full h-auto object-contain max-h-[28rem]"
                style={{ aspectRatio: 'auto' }}
              />
            </div>
            <div className="flex-1 min-h-[1rem] print:min-h-[2rem]" aria-hidden="true" />
            <VmkQuotePageFooter pageNumber={1} quoteNumber={quoteNumber} clientName={clientDetails.name} />
          </div>
          </div>

          </>
          )}

          {showLetterAndSpecsPages && (
          <>
          {/* Page 2: Letter */}
          {(() => {
            const recipientLastname = clientDetails.name.trim().split(/\s+/).pop() || '';
            return (
              <div className="quote-pdf-page mb-4 quote-letterhead-page">
                <div className="quote-pdf-page-inner border-2 border-black box-border flex flex-col">
                  <div
                    className="quote-letter-content flex-1 leading-relaxed text-gray-800 space-y-3 overflow-hidden"
                    style={{ fontSize: '13px' }}
                  >
                    <p>Dear Mr./Ms. {recipientLastname || '[Last name]'},</p>
                    <p>Thank you for the pleasant conversation during our advisor&apos;s visit. We have discussed your wishes and ideas with you and look forward to transforming them into a sustainable and aesthetic solution for your home.</p>
                    <p>Based on our visit and the information you shared, we are pleased to offer you a proposal for the installation of high-quality uPVC facade elements.</p>
                    <p className="font-semibold mt-4">Our Approach</p>
                    <p>We work exclusively with top-quality uPVC facade elements. These are low-maintenance, weather-resistant, and offer excellent insulation values.</p>
                    <p><span className="font-semibold">Customization:</span> Each element is completely custom-made to match the appearance of your home and your personal wishes.</p>
                    <p><span className="font-semibold">Professional Installation:</span> Our experienced installers ensure professional installation, with precision and an eye for detail being paramount.</p>
                    <p className="font-semibold mt-4">Advantages of uPVC Facade Elements</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Long lifespan and minimal maintenance costs.</li>
                      <li>Available in various colors and designs, for a modern or classic look.</li>
                      <li>Excellent insulation, which contributes to lower energy bills.</li>
                      <li>Resistant to all weather conditions and colorfast.</li>
                    </ul>
                    <p className="font-semibold mt-4">Warranty</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>20-year manufacturer&apos;s warranty on uPVC window frames</li>
                      <li>10-year supplier&apos;s warranty on glass</li>
                      <li>10-year supplier&apos;s warranty on window and door hardware</li>
                      <li>10-year warranty on installation</li>
                    </ul>
                    <p className="font-semibold mt-4">Police-approved safe living certification</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>SKG2 certification</li>
                    </ul>
                    <p className="font-semibold mt-4">The Steps</p>
                    <p>If you accept this quote, we will:</p>
                    <ol className="list-decimal pl-6 space-y-1">
                      <li>Schedule a final measurement appointment to confirm the exact measurements.</li>
                      <li>Work with you to create a schedule for the project.</li>
                      <li>Carry out our work within the agreed timeframe.</li>
                    </ol>
                    <p>We stand for quality and service, and we look forward to enhancing your home and making it more sustainable with our uPVC facade elements. If you have any questions or require further information, please do not hesitate to contact us.</p>
                  </div>
                  <VmkQuotePageFooter pageNumber={2} quoteNumber={quoteNumber} clientName={clientDetails.name} />
                </div>
              </div>
            );
          })()}

          {/* Page 3: Additional Details */}
          <div className="quote-pdf-page mb-4 quote-letterhead-page">
            <div className="quote-pdf-page-inner border-2 border-black box-border flex flex-col">
              <div className="quote-page2-content text-base flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-800 underline mb-4">{t('quote.specifications')}</h3>
              <div className="mb-6 text-gray-800 leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">{t('quote.specFrameType')}</p>
                  <p className="mt-2">{t('quote.specFrameTypeText')}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">{t('quote.specTypeGlass')}</p>
                  <p className="mt-2">{t('quote.specTypeGlassText')}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">{t('quote.specLocks')}</p>
                  <p className="mt-2">{t('quote.specLocksText')}</p>
                </div>
              </div>

              {(isEditing && !isExportingPDF) ? (
              <>
                <div className="mb-6">
                  <h4 className="text-base font-semibold mb-3 text-gray-700">{t('quote.homeFinishing')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <YesNoToggle label={t('quote.typesetting')} value={clientDetails.typesetting} onChange={(val) => setClientDetails({...clientDetails, typesetting: val})} />
                    <YesNoToggle label={t('quote.container')} value={clientDetails.container} onChange={(val) => setClientDetails({...clientDetails, container: val})} />
                    <YesNoToggle label={t('quote.interiorFinish')} value={clientDetails.interiorFinish} onChange={(val) => setClientDetails({...clientDetails, interiorFinish: val})} />
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="text-base font-semibold mb-3 text-gray-700">{t('quote.liftingSupport')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <YesNoToggle label={t('quote.crane')} value={clientDetails.crane} onChange={(val) => setClientDetails({...clientDetails, crane: val})} />
                    <YesNoToggle label={t('quote.liftingAssistance')} value={clientDetails.liftingAssistance} onChange={(val) => setClientDetails({...clientDetails, liftingAssistance: val})} />
                  </div>
                </div>
                {isAuthenticated && (
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 print:hidden">
                    <div className="space-y-4">
                      <YesNoToggle label={t('quote.applyDiscount') || 'Apply discount'} value={savedQuoteDiscountEnabled} onChange={setSavedQuoteDiscountEnabled} />
                      {savedQuoteDiscountEnabled && (
                        <div className="pl-2">
                          <Label htmlFor="saved-quote-discount-percent" className="text-xs text-gray-600 block mb-1">{t('quote.discountPercentage') || 'Discount (%)'}</Label>
                          <Input id="saved-quote-discount-percent" type="number" min="0" max="100" step="0.5" value={savedQuoteDiscountPercentage} onChange={(e) => setSavedQuoteDiscountPercentage(parseFloat(e.target.value) || 0)} className="max-w-[120px] h-8 text-sm" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="mb-6">
                  <Label htmlFor="comments" className="font-semibold mb-3 block text-gray-700">{t('quote.comments')}</Label>
                  <Textarea id="comments" value={clientDetails.comments || ''} onChange={(e) => setClientDetails({...clientDetails, comments: e.target.value})} placeholder={t('quote.commentsPlaceholder')} className="text-base min-h-[80px]" rows={4} />
                </div>
              </>
              ) : (
              <div className="text-gray-700 space-y-5">
                <div>
                  <p className="font-semibold mb-3 text-gray-700">{t('quote.homeFinishing')}</p>
                  <div className="quote-yesno-list">
                    <span className="quote-yesno-label">{t('quote.typesetting')}:</span>
                    <span className={`quote-yesno-value font-medium ${clientDetails.typesetting ? 'text-green-600' : 'text-red-600'}`}>{clientDetails.typesetting ? 'YES' : 'NO'}</span>
                    <span className="quote-yesno-label">{t('quote.container')}:</span>
                    <span className={`quote-yesno-value font-medium ${clientDetails.container ? 'text-green-600' : 'text-red-600'}`}>{clientDetails.container ? 'YES' : 'NO'}</span>
                    <span className="quote-yesno-label">{t('quote.interiorFinish')}:</span>
                    <span className={`quote-yesno-value font-medium ${clientDetails.interiorFinish ? 'text-green-600' : 'text-red-600'}`}>{clientDetails.interiorFinish ? 'YES' : 'NO'}</span>
                  </div>
                </div>
                <div>
                  <p className="font-semibold mb-3 text-gray-700">{t('quote.liftingSupport')}</p>
                  <div className="quote-yesno-list">
                    <span className="quote-yesno-label">{t('quote.crane')}:</span>
                    <span className={`quote-yesno-value font-medium ${clientDetails.crane ? 'text-green-600' : 'text-red-600'}`}>{clientDetails.crane ? 'YES' : 'NO'}</span>
                    <span className="quote-yesno-label">{t('quote.liftingAssistance')}:</span>
                    <span className={`quote-yesno-value font-medium ${clientDetails.liftingAssistance ? 'text-green-600' : 'text-red-600'}`}>{clientDetails.liftingAssistance ? 'YES' : 'NO'}</span>
                    {clientDetails.liftingAssistance === true && <span className="quote-yesno-fee text-gray-600">{t('quote.liftingAssistanceFee')}</span>}
                  </div>
                </div>
                {clientDetails.comments && (
                  <div>
                    <p className="font-semibold mb-3 text-gray-700">{t('quote.comments')}</p>
                    <p className="whitespace-pre-wrap text-gray-800">{clientDetails.comments}</p>
                  </div>
                )}
              </div>
              )}
              </div>
            <VmkQuotePageFooter pageNumber={3} quoteNumber={quoteNumber} clientName={clientDetails.name} />
            </div>
          </div>
          </>
          )}

          {showVmkTemplatePages && templatePageNumbers && (
            <VmkQuoteTemplateFront
              pageNumbers={templatePageNumbers}
              quoteNumber={quoteNumber}
              clientDetails={clientDetails}
              advisorName={advisorName}
              quoteDate={activeBackendQuote?.created_at}
              validUntil={activeBackendQuote?.valid_until}
              savedQuotes={savedQuotes}
              includeNijBegun={includeNijBegunInExport}
              t={t}
            />
          )}

          {/* VMK-style pricing tables — drawing pages (page 4+ in 9-page export) */}
          {vmkPricingPages}

          {showVmkTemplatePages && templatePageNumbers && (
            <VmkQuoteTemplateBack
              pageNumbers={templatePageNumbers}
              quoteNumber={quoteNumber}
              clientDetails={clientDetails}
              totalPrice={totalPrice}
              totalAreaSqm={totalArea}
              formatPrice={formatPrice}
              getQuoteLineTotal={getQuoteLineTotal}
              savedQuotes={savedQuotes}
              sectionSummaries={sectionSummaries}
              multiSectionMode={multiSectionMode}
              includeNijBegun={includeNijBegunInExport}
              signedAgreement={activeBackendQuote?.signedAgreement || null}
              t={t}
            />
          )}

          {showInvestmentPageOnly && (
            <VmkInvestmentPage
              pageNumber={drawingPageCount + 1}
              quoteNumber={quoteNumber}
              clientDetails={clientDetails}
              totalPrice={totalPrice}
              totalAreaSqm={totalArea}
              formatPrice={formatPrice}
              getQuoteLineTotal={getQuoteLineTotal}
              savedQuotes={savedQuotes}
              sectionSummaries={sectionSummaries}
              multiSectionMode={multiSectionMode}
              includeNijBegun={includeNijBegunInExport}
              t={t}
            />
          )}

          {showLetterAndSpecsPages && (
          <>
          {/* Guarantees */}
          <div className="quote-pdf-page mb-4 quote-letterhead-page">
            <div className="quote-pdf-page-inner border-2 border-black box-border flex flex-col">
              <div className="flex-shrink-0 ml-8 md:ml-16 pl-2">
                <h3 className="text-lg font-bold text-gray-800 mb-6">{t('quote.page4VmkGuaranteesHeading')}</h3>
                <ul className="list-none space-y-5 mb-10">
                  <li className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded border-2 border-gray-700 flex items-center justify-center bg-gray-50"><Shield className="w-4 h-4 text-gray-700" /></span>
                    <span>{t('quote.page4Quality')}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded border-2 border-gray-700 flex items-center justify-center bg-gray-50"><Wallet className="w-4 h-4 text-gray-700" /></span>
                    <span>{t('quote.page4GuaranteeFund')}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded border-2 border-gray-700 flex items-center justify-center bg-gray-50"><Eye className="w-4 h-4 text-gray-700" /></span>
                    <span>{t('quote.page4ControlTransparency')}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded border-2 border-gray-700 flex items-center justify-center bg-gray-50"><Leaf className="w-4 h-4 text-gray-700" /></span>
                    <span>{t('quote.page4Sustainability')}</span>
                  </li>
                </ul>
                <h3 className="text-lg font-bold text-gray-800 mb-6">{t('quote.page4GuaranteesHeading')}</h3>
                <ul className="list-none space-y-4 text-gray-800 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee4')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee5')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee6')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee7')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee8')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded border-2 border-green-600 flex items-center justify-center mt-0.5 bg-green-50"><Check className="w-3 h-3 text-green-700 stroke-[2.5]" /></span>
                    <span>{t('quote.page4Guarantee9')}</span>
                  </li>
                </ul>
              </div>
              <div className="flex-1 min-h-[1rem] print:min-h-[2rem]" aria-hidden="true" />
              <VmkQuotePageFooter pageNumber={guaranteesPageNumber} quoteNumber={quoteNumber} clientName={clientDetails.name} />
            </div>
          </div>
          </>
          )}

          {isEditing && !asPage && (
            <div className="mt-6 mb-4 print:hidden px-4">
              <Button onClick={handleSavePreview} className="bg-orange-600 hover:bg-orange-700 h-9 text-sm px-5">
                {t('quote.savePreview')}
              </Button>
            </div>
          )}
        </>
  );

  const quoteDocument = (
    <div
      ref={quoteRef}
      className={`quote-content-root bg-slate-100 print:bg-white ${asPage ? 'py-0 print:py-0' : 'py-4 print:py-0'}`}
      id="quote-content"
    >
      <QuotePreviewScaler className="quote-export-preview-scaler">
        {quotePagesBody}
      </QuotePreviewScaler>
    </div>
  );

  const quoteMain = (
    <>
      {quoteDocument}
      {quoteActionBar}
    </>
  );

  if (asPage) {
    return (
      <div className="min-h-screen bg-slate-100 print:bg-white">
        <header className="print:hidden sticky top-0 z-20 bg-slate-800 text-white px-4 sm:px-6 py-3 sm:py-4 shadow-lg">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <button
                type="button"
                onClick={goBackToList}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-600 text-sm text-white hover:bg-slate-700 transition-colors shrink-0"
              >
                <span className="mr-1">←</span>
                {t('configurator.back')}
              </button>
              <h1 className="text-lg sm:text-xl font-bold truncate">
                {dashboardMode
                  ? (activeBackendQuote?.quoteNumber || t('dashboard.viewQuote') || 'Quote')
                  : t('configurator.exportQuote')}
              </h1>
              {hasQuoteContent ? (
                <span className="inline-flex items-center rounded-full bg-slate-700 px-2.5 py-1 text-xs font-semibold text-white tabular-nums shrink-0">
                  {t('summary.totalDrawings', {
                    count: countQuoteDrawings(quoteSections, savedQuotes),
                  })}
                </span>
              ) : null}
            </div>
            <ConfiguratorNavActions
              showExport={!forceEditing || dashboardMode}
              showReset={false}
              buttonClassName="px-2.5 py-1.5 sm:px-3 sm:py-1.5 border border-slate-500 hover:bg-slate-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
              exportButtonClassName="px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
              subsidieButtonClassName="px-2.5 py-1.5 sm:px-3 sm:py-1.5 border border-sky-400/70 bg-sky-600/20 hover:bg-sky-600/35 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
              quotesButtonClassName="inline-flex px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
            />
          </div>
        </header>

        <main className="max-w-[1400px] mx-auto px-4 py-6 print:max-w-none print:px-0 print:py-0">
          {quoteIsSigned ? (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 print:hidden">
              {t('quote.quoteLockedSigned')}
            </div>
          ) : saveAsNewQuote ? (
            <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 print:hidden">
              {t('quote.quoteAlreadySentBanner')}
            </div>
          ) : null}
          {!hasQuoteContent ? (
            <div className="bg-white rounded-lg shadow-md p-10 text-center">
              <p className="text-gray-600 mb-6">{t('savedQuotes.noQuotes')}</p>
              <Button onClick={goBackToList} className="bg-orange-600 hover:bg-orange-700">
                {t('configurator.back')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_19rem] xl:grid-cols-[minmax(0,1fr)_21rem] gap-6 items-start print:block">
              <div className="min-w-0 order-1">
                <div className="quote-export-preview-panel bg-white shadow-xl rounded-lg print:shadow-none print:rounded-none print:overflow-visible overflow-hidden">
                  {quoteDocument}
                </div>
              </div>
              <aside className="print:hidden order-2 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto">
                <div className="rounded-xl border border-slate-200 bg-white shadow-md">
                  {quoteActionBar}
                </div>
              </aside>
            </div>
          )}
        </main>
        {sendEmailDialog}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto print:max-h-none print:overflow-visible print:border-0 print:shadow-none print:p-0 sm:w-full">
        <DialogHeader className="print:hidden">
          <DialogTitle>{t('configurator.exportQuote')}</DialogTitle>
        </DialogHeader>
        {quoteMain}
      </DialogContent>
      {sendEmailDialog}
    </Dialog>
  );
};

export default SavedQuotes;
